import { formatDate, formatDateTime } from "@/utils/dateTime";
import { useRef, useState, useEffect } from "react";
import type { ReactElement, ReactNode } from "react";
import {
  Button,
  Card,
  DatePicker,
  Descriptions,
  Form,
  Image,
  Input,
  message,
  Modal,
  Select,
  Space,
  Switch,
  Tag,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { Dayjs } from "dayjs";
import PageTable from "@/components/i-pageTable/i-pageTable";
import {
  approveMerchantApplication,
  disableMerchant,
  enableMerchant,
  getMerchantApplication,
  listMerchantApplications,
  rejectMerchantApplication,
} from "@/api/shopMerchant";
import { listGoodsCategories } from "@/api/shopGoods";
import type { GoodsCategoryOption } from "@/types/shopGoods";
import type {
  MerchantApplication,
  MerchantApproval,
  MerchantApplicationDetail,
  MerchantApplyQuery,
} from "@/types/shopMerchant";
import { useShopList } from "../useShopList";

interface SearchValues {
  applicantAccount?: string;
  merchantName?: string;
  auditStatus?: number;
  range?: [Dayjs, Dayjs];
}

const initialQuery: MerchantApplyQuery = { ypageNum: 1, ypageSize: 10 };
const statuses = ["待审核", "审核通过", "审核驳回"];
interface MerchantDetailField {
  key: string;
  label: string | ((detail: MerchantApplicationDetail) => string);
  dataIndex?: keyof MerchantApplicationDetail;
  getValue?: (detail: MerchantApplicationDetail) => unknown;
  image?: boolean;
  render?: (value: unknown) => string;
  hide?: (value: unknown, detail: MerchantApplicationDetail) => boolean;
}

interface MerchantDetailGroup {
  title: string;
  fields: MerchantDetailField[];
}

type CategoryNameMap = Record<string, string>;

function formatRegion(detail: MerchantApplicationDetail): string {
  const regionNames = [detail.provinceName, detail.cityName, detail.districtName]
    .filter((value): value is string => Boolean(value?.trim()));
  if (regionNames.length) return regionNames.join(" / ");
  return [detail.province, detail.city, detail.district]
    .filter((value) => value != null)
    .join(" / ");
}

const detailGroups: MerchantDetailGroup[] = [
  {
    title: "商家基本信息",
    fields: [
      { key: "applicantAccount", dataIndex: "applicantAccount", label: "申请人账号" },
      { key: "applicantNickname", dataIndex: "applicantNickname", label: "申请人昵称" },
      { key: "merchantName", dataIndex: "merchantName", label: "商店名称" },
      { key: "merchantPhone", dataIndex: "merchantPhone", label: "商家联系电话" },
      { key: "contactName", dataIndex: "contactName", label: "联系人" },
      { key: "contactPhone", dataIndex: "contactPhone", label: "联系人电话" },
      { key: "recommenderBusinessId", dataIndex: "recommenderBusinessId", label: "推荐人业务ID" },
      { key: "recommender", dataIndex: "recommender", label: "推荐人" },
      { key: "auditStatus", dataIndex: "auditStatus", label: "审核状态" },
      { key: "auditRemark", dataIndex: "auditRemark", label: "审核备注" },
      { key: "applyTime", dataIndex: "applyTime", label: "申请时间", render: formatDateTime },
      { key: "auditTime", dataIndex: "auditTime", label: "审核时间", render: formatDateTime },
    ],
  },
  {
    title: "业务经营信息",
    fields: [
      { key: "businessType", dataIndex: "businessType", label: "业务经营类型" },
      { key: "businessProductType", dataIndex: "businessProductType", label: "经营产品类型" },
      {
        key: "registeredCapital",
        dataIndex: "registeredCapital",
        label: "注册资本（万元）",
        hide: (value): boolean => Number(value) === 0,
      },
      { key: "companyType", dataIndex: "companyType", label: "企业类型" },
      { key: "establishDate", dataIndex: "establishDate", label: "成立日期", render: formatDate },
      { key: "businessLicense", dataIndex: "businessLicense", label: "营业执照号" },
      { key: "businessLicenseImg", dataIndex: "businessLicenseImg", label: "营业执照", image: true },
      {
        key: "idCardFrontImg",
        dataIndex: "idCardFrontImg",
        label: (detail): string => detail.businessType === 1 ? "经营者身份证正面" : "法人身份证正面",
        image: true,
      },
      {
        key: "idCardBackImg",
        dataIndex: "idCardBackImg",
        label: (detail): string => detail.businessType === 1 ? "经营者身份证反面" : "法人身份证反面",
        image: true,
      },
      {
        key: "idCardNo",
        dataIndex: "idCardNo",
        label: (detail): string => detail.businessType === 1 ? "经营者身份证号" : "法人身份证号",
      },
      { key: "bankName", dataIndex: "bankName", label: "开户银行" },
      { key: "bankBranch", dataIndex: "bankBranch", label: "开户支行" },
      { key: "bankAccount", dataIndex: "bankAccount", label: "银行账号" },
    ],
  },
  {
    title: "退货信息",
    fields: [
      { key: "region", label: "退货地区", getValue: formatRegion },
      { key: "detailAddress", dataIndex: "detailAddress", label: "退货详细地址" },
      { key: "receiverName", dataIndex: "receiverName", label: "收件人" },
      { key: "receiverPhone", dataIndex: "receiverPhone", label: "收件电话" },
    ],
  },
];

function getDetailValue(
  field: MerchantDetailField,
  detail: MerchantApplicationDetail
): unknown {
  return field.getValue?.(detail) ??
    (field.dataIndex ? detail[field.dataIndex] : undefined);
}

function getDetailLabel(
  field: MerchantDetailField,
  detail: MerchantApplicationDetail
): string {
  return typeof field.label === "function" ? field.label(detail) : field.label;
}

function createCategoryNameMap(categories: GoodsCategoryOption[]): CategoryNameMap {
  return Object.fromEntries(
    categories.map((category) => [String(category.id), category.categoryName])
  );
}

function formatBusinessProductType(
  value: unknown,
  categoryNames: CategoryNameMap
): string {
  const categoryIds = String(value).split(",")
    .map((categoryId) => categoryId.trim())
    .filter(Boolean);
  return categoryIds.map((categoryId) => categoryNames[categoryId] ?? categoryId).join("、");
}

function hasDetailValue(value: unknown): boolean {
  return value !== null && value !== undefined &&
    (typeof value !== "string" || value.trim() !== "");
}

function renderDetailValue(
  field: MerchantDetailField,
  detail: MerchantApplicationDetail,
  value: unknown,
  categoryNames: CategoryNameMap
): ReactNode {
  if (field.image) return <Image width={140} src={String(value)} />;
  if (field.key === "businessType") {
    return detail.businessType === 1 ? "个体工商户" : "企业";
  }
  if (field.key === "businessProductType") {
    return formatBusinessProductType(value, categoryNames);
  }
  if (field.key === "auditStatus") {
    return (
      <Tag color={["orange", "green", "red"][detail.auditStatus] ?? "default"}>
        {detail.auditStatusName || statuses[detail.auditStatus] || "未知"}
      </Tag>
    );
  }
  return field.render ? field.render(value) : String(value);
}

function MerchantReview(): ReactElement {
  const [form] = Form.useForm<SearchValues>();
  const [approveForm] = Form.useForm<Omit<MerchantApproval, "id">>();
  const [approveOpen, setApproveOpen] = useState(false);
  const [reviewTarget, setReviewTarget] = useState<MerchantApplication | null>(null);
  const [rejectForm] = Form.useForm<{ auditRemark: string }>();
  const [query, setQuery] = useState(initialQuery);
  const { rows, total, loading, refresh } = useShopList(
    listMerchantApplications,
    query
  );
  const [detail, setDetail] = useState<MerchantApplicationDetail | null>(null);
  const [categoryNames, setCategoryNames] = useState<CategoryNameMap>({});
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [statusTarget, setStatusTarget] = useState<MerchantApplication | null>(null);
  const statusSubmitting = useRef(false);
  const detailRequest = useRef(0);
  useEffect(
    () => (): void => {
      detailRequest.current += 1;
    },
    []
  );

  const handleViewDetail = async (
    record: MerchantApplication
  ): Promise<void> => {
    const requestId = ++detailRequest.current;
    setDetail(null);
    setCategoryNames({});
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const [response, categoryResponse] = await Promise.all([
        getMerchantApplication(record.id),
        listGoodsCategories().catch(() => null),
      ]);
      if (requestId === detailRequest.current) {
        if (categoryResponse) {
          setCategoryNames(createCategoryNameMap(categoryResponse.data ?? []));
        }
        setDetail(response.data);
      }
    } catch {
      /* 请求层展示后端错误。 */
    } finally {
      if (requestId === detailRequest.current) setDetailLoading(false);
    }
  };
  const handleCloseDetail = (): void => {
    detailRequest.current += 1;
    setDetailOpen(false);
    setDetail(null);
  };
  const handleOpenApprove = (record: MerchantApplication): void => {
    if (record.auditStatus !== 0 || submitting) return;
    setReviewTarget(record);
    approveForm.resetFields();
    setApproveOpen(true);
  };
  const handleApprove = async (
    values: Omit<MerchantApproval, "id">
  ): Promise<void> => {
    if (!reviewTarget || reviewTarget.auditStatus !== 0 || submitting) return;
    setSubmitting(true);
    try {
      await approveMerchantApplication({
        id: reviewTarget.id,
        auditRemark: values.auditRemark?.trim(),
      });
      message.success("入驻申请已通过");
      setApproveOpen(false);
      setReviewTarget(null);
      refresh();
    } catch {
      // 请求层展示后端错误，保留表单便于重试。
    } finally {
      setSubmitting(false);
    }
  };
  const handleReject = async (values: {
    auditRemark: string;
  }): Promise<void> => {
    if (!reviewTarget || reviewTarget.auditStatus !== 0 || submitting) return;
    setSubmitting(true);
    try {
      await rejectMerchantApplication(reviewTarget.id, values.auditRemark.trim());
      message.success("入驻申请已驳回");
      setRejectOpen(false);
      setReviewTarget(null);
      refresh();
    } catch {
      /* 请求层展示后端错误，保留表单便于重试。 */
    } finally {
      setSubmitting(false);
    }
  };
  const handleOpenReject = (record: MerchantApplication): void => {
    if (record.auditStatus !== 0 || submitting) return;
    setReviewTarget(record);
    rejectForm.resetFields();
    setRejectOpen(true);
  };
  const handleOpenStatusChange = (record: MerchantApplication): void => {
    if (submitting || loading || record.auditStatus !== 1 || !record.merchantId || record.merchantId === "0" ||
      (record.merchantStatus !== 0 && record.merchantStatus !== 1)) return;
    setStatusTarget(record);
  };
  const handleCloseStatusChange = (): void => {
    if (!statusSubmitting.current) setStatusTarget(null);
  };
  const handleConfirmStatusChange = async (): Promise<void> => {
    if (!statusTarget?.merchantId || statusSubmitting.current) return;
    statusSubmitting.current = true;
    setSubmitting(true);
    try {
      const disabling = statusTarget.merchantStatus === 1;
      await (disabling ? disableMerchant : enableMerchant)(statusTarget.merchantId);
      message.success(disabling ? "商家已禁用" : "商家已启用");
      setStatusTarget(null);
      refresh();
    } catch {
      // 请求层原样展示商品、订单等校验错误，保持列表状态不变。
    } finally {
      statusSubmitting.current = false;
      setSubmitting(false);
    }
  };
  const handleSearch = (values: SearchValues): void => {
    setQuery({
      ...initialQuery,
      ypageSize: query.ypageSize,
      merchantName: values.merchantName?.trim() || undefined,
      applicantAccount: values.applicantAccount?.trim() || undefined,
      auditStatus: values.auditStatus,
      startTime: values.range?.[0]?.format("YYYY-MM-DD HH:mm:ss"),
      endTime: values.range?.[1]?.format("YYYY-MM-DD HH:mm:ss"),
    });
  };
  const handleReset = (): void => {
    form.resetFields();
    setQuery({ ...initialQuery });
  };
  const columns: ColumnsType<MerchantApplication> = [
    { title: "申请记录ID", dataIndex: "id" },
    { title: "商家ID", dataIndex: "merchantId" },
    { title: "申请人ID", dataIndex: "appUserId" },
    { title: "申请人账号", dataIndex: "applicantAccount" },
    { title: "申请人昵称", dataIndex: "applicantNickname" },
    { title: "商户名称", dataIndex: "merchantName" },
    {
      title: "商家可提现收益",
      dataIndex: "availableAmount",
      render: (value?: number | null): string =>
        value == null ? "-" : `¥${Number(value).toFixed(2)}`,
    },
    {
      title: "商家冻结收益",
      dataIndex: "frozenAmount",
      render: (value?: number | null): string =>
        value == null ? "-" : `¥${Number(value).toFixed(2)}`,
    },
    {
      title: "业务经营类型",
      render: (_, record): ReactElement => (
        <Tag color={record.businessType === 1 ? "blue" : "purple"}>
          {record.businessTypeName || (record.businessType === 1 ? "个人" : "企业")}
        </Tag>
      ),
    },
    { title: "联系电话", dataIndex: "contactPhone" },
    { title: "推荐人业务ID", dataIndex: "recommenderBusinessId" },
    { title: "商家推荐人", dataIndex: "recommender" },
    {
      title: "审核状态",
      render: (_, record): ReactElement => (
        <Tag color={["orange", "green", "red"][record.auditStatus] ?? "default"}>
          {record.auditStatusName || statuses[record.auditStatus] || "未知"}
        </Tag>
      ),
    },
    {
      title: "商家状态",
      render: (_, record): ReactNode => {
        if (record.auditStatus !== 1 || !record.merchantId || record.merchantId === "0") return "-";
        if (record.merchantStatus === 2) return <Tag>注销</Tag>;
        if (record.merchantStatus !== 0 && record.merchantStatus !== 1) return "-";
        return (
          <Switch
            checked={record.merchantStatus === 1}
            checkedChildren="正常"
            unCheckedChildren="禁用"
            aria-label={`${record.merchantName}商家状态`}
            disabled={submitting || loading}
            loading={submitting && statusTarget?.id === record.id}
            onChange={() => handleOpenStatusChange(record)}
          />
        );
      },
    },
    { title: "审核备注", dataIndex: "auditRemark" },
    { title: "申请时间", dataIndex: "applyTime", render: formatDateTime },
    { title: "审核时间", dataIndex: "auditTime", render: formatDateTime },
    {
      title: "操作",
      fixed: "right",
      render: (_, record): ReactElement => (
        <Space>
          <Button
            size="small"
            onClick={() => void handleViewDetail(record)}
          >
            详情
          </Button>
          {record.auditStatus === 0 && (
            <>
              <Button
                size="small"
                type="primary"
                disabled={submitting}
                onClick={() => handleOpenApprove(record)}
              >
                通过
              </Button>
              <Button
                size="small"
                danger
                disabled={submitting}
                onClick={() => handleOpenReject(record)}
              >
                驳回
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Form
        form={form}
        layout="inline"
        onFinish={handleSearch}
        style={{ gap: 12 }}
      >
        <Form.Item name="applicantAccount" label="账号">
          <Input allowClear placeholder="请输入账号" />
        </Form.Item>
        <Form.Item name="merchantName" label="商户名称">
          <Input allowClear placeholder="请输入商户名称" />
        </Form.Item>
        <Form.Item name="auditStatus" label="审核状态">
          <Select
            allowClear
            placeholder="请选择审核状态"
            style={{ width: 140 }}
            options={statuses.map((label, value) => ({ label, value }))}
          />
        </Form.Item>
        <Form.Item name="range" label="申请时间">
          <DatePicker.RangePicker showTime />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit">
            查询
          </Button>
          <Button onClick={handleReset}>重置</Button>
        </Space>
      </Form>
      <PageTable
        rowKey="id"
        columns={columns}
        data={rows}
        loading={loading}
        pageChange={(ypageNum, ypageSize) =>
          setQuery((current) => ({ ...current, ypageNum, ypageSize }))
        }
        pagination={{
          current: query.ypageNum,
          pageSize: query.ypageSize,
          total,
        }}
      />
      <Modal
        open={statusTarget !== null}
        title={statusTarget?.merchantStatus === 1 ? "禁用商家" : "启用商家"}
        okText={statusTarget?.merchantStatus === 1 ? "确认禁用" : "确认启用"}
        cancelText="取消"
        onOk={handleConfirmStatusChange}
        onCancel={handleCloseStatusChange}
        confirmLoading={submitting}
        okButtonProps={{ danger: statusTarget?.merchantStatus === 1 }}
        cancelButtonProps={{ disabled: submitting }}
        closable={!submitting}
        maskClosable={false}
        keyboard={!submitting}
      >
        {statusTarget?.merchantStatus === 1 ? (
          <p>确认禁用“{statusTarget.merchantName}”？禁用后，该商家将无法使用账号密码登录商家管理后台。</p>
        ) : (
          <p>确认启用“{statusTarget?.merchantName}”？启用后，该商家可恢复登录商家管理后台。</p>
        )}
      </Modal>
      <Modal
        open={detailOpen}
        title="入驻商家详情"
        width={900}
        onCancel={handleCloseDetail}
        footer={null}
      >
        {detailLoading ? (
          "加载中…"
        ) : detail ? (
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {detailGroups.map((group) => (
              <Card key={group.title} title={group.title} size="small">
                <Descriptions bordered column={2} size="small">
                  {group.fields.map((field) => {
                    const value = getDetailValue(field, detail);
                    if (!hasDetailValue(value) || field.hide?.(value, detail)) return null;
                    return (
                      <Descriptions.Item key={field.key} label={getDetailLabel(field, detail)}>
                        {renderDetailValue(field, detail, value, categoryNames)}
                      </Descriptions.Item>
                    );
                  })}
                </Descriptions>
              </Card>
            ))}
          </Space>
        ) : null}
      </Modal>
      <Modal
        open={approveOpen}
        title="通过入驻申请"
        okText="确认通过"
        onCancel={() => {
          if (!submitting) {
            setApproveOpen(false);
            setReviewTarget(null);
          }
        }}
        onOk={() => approveForm.submit()}
        confirmLoading={submitting}
        cancelButtonProps={{ disabled: submitting }}
        closable={!submitting}
        maskClosable={false}
        keyboard={!submitting}
      >
        <Form
          form={approveForm}
          onFinish={handleApprove}
          layout="vertical"
          disabled={submitting}
        >
          <Form.Item name="auditRemark" label="审核备注" style={{ marginTop: 16 }}>
            <Input.TextArea rows={3} placeholder="请输入审核备注（选填）" />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        open={rejectOpen}
        title="驳回入驻申请"
        okText="确认驳回"
        onCancel={() => {
          if (!submitting) {
            setRejectOpen(false);
            setReviewTarget(null);
          }
        }}
        onOk={() => rejectForm.submit()}
        confirmLoading={submitting}
        cancelButtonProps={{ disabled: submitting }}
        closable={!submitting}
        maskClosable={!submitting}
        keyboard={!submitting}
      >
        <p>确认驳回“{reviewTarget?.merchantName}”的入驻申请？</p>
        <Form form={rejectForm} onFinish={handleReject} layout="vertical" disabled={submitting}>
          <Form.Item
            name="auditRemark"
            label="驳回原因"
            rules={[
              { required: true, whitespace: true, message: "请输入驳回原因" },
            ]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
export default MerchantReview;
